import React, {
  type ComponentType,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import {
  type AttachedRuleItem,
  DEFAULT_FORM_OFFSET,
  DEFAULT_REMINDER_OFFSET,
  formatRuleTiming,
  FormsRuleEditor,
  RemindersRuleEditor,
} from '@features/appointments/components/shared/appointmentRuleEditors';
import {
  EditorOptionList,
  PropertyEditorSearchFrame,
} from '@features/appointments/components/shared/PropertyEditorSearchFrame';
import { PlaceOfServiceSelect } from '@features/billing/components/Claim/PlaceOfServiceSelect';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useBundleCatalog } from '@features/subscriptions';
import { useTelehealthAccess } from '@features/telehealth';
import { formatBillingDefaultsLabel } from '../utils/billingDefaults';
import { formatVisitModeLabel } from '../utils/visitMode';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import {
  AccessTimeOutlined,
  ArrowBack,
  ArticleOutlined,
  AssignmentTurnedInOutlined,
  Close,
  LabelOutlined,
  LocalHospitalOutlined,
  PaletteOutlined,
  ReceiptLongOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { LabelPicker } from '@features/appointment-labels';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Stack,
  type SvgIconProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import {
  doCptCodesMatch,
  getModalityPlaceOfServiceError,
  normalizeCptCodes,
} from '@utils/modalityPlaceOfService';
import { useSnackbar } from 'notistack';

import { type UserTemplateItem } from '../../../types/types';
import { useAppointmentTypeTemplates, useAvailableColors, useTemplateNameById } from '../hooks';
import {
  useCreateAppointmentType,
  useCreateOrganizationAppointmentType,
  useDeleteAppointmentType,
  useDeleteOrganizationAppointmentType,
  useUpdateAppointmentType,
  useUpdateOrganizationAppointmentType,
} from '../store/hooks';
import {
  type AppointmentType,
  type CreateAppointmentTypeRequest,
  type UpdateAppointmentTypeRequest,
} from '../types';
import { canMutateAppointmentType, getApiErrorMessage } from '../utils/permissions';

import { ColorDot } from './ColorDot';
import { type ExpandableFieldItem } from './ExpandableField';

interface FormScheduleItem extends ExpandableFieldItem {
  form_definition_id: string;
  name: string;
  offset: string;
  timing?: string;
  title?: string;
}

interface ReminderItem extends ExpandableFieldItem {
  reminder_template_id: string;
  name: string;
  offset: string;
  timing?: string;
}

interface FormOverrideItem extends ExpandableFieldItem {
  form_definition_id?: string;
}

interface ReminderOverrideItem extends ExpandableFieldItem {
  reminder_template_id?: string;
}

interface AppointmentTypeFormData {
  name: string;
  color: string;
  defaultTemplateId: string | null;
  defaultTemplate: UserTemplateItem | null;
  defaultDuration: number | null;
  telehealth: boolean;
  defaultModality: 'in_person' | 'video' | 'audio' | null;
  defaultUseJotmeet: boolean;
  defaultPlaceOfService: string | null;
  defaultCptCodes: string[];
  selectedForms: FormScheduleItem[];
  selectedReminders: ReminderItem[];
  defaultLabelIds: string[];
}

interface AppointmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (appointmentType: AppointmentType) => void;
  type: 'create' | 'edit';
  // For create mode
  initialName?: string;
  defaultValues?: {
    basedOnAppointmentType?: AppointmentType;
    overrides?: {
      duration?: number;
      templateId?: string;
      telehealth?: boolean;
      useJotmeet?: boolean;
      forms?: ExpandableFieldItem[];
      reminders?: ExpandableFieldItem[];
    };
  };
  // For edit mode
  appointmentType?: AppointmentType | null;
  // For duplicate name validation — passed from parent to avoid extra subscription
  existingAppointmentTypes?: AppointmentType[];
  scope?: 'provider' | 'organization';
  organizationId?: string | null;
  canDelete?: boolean;
  inline?: boolean;
  onBack?: () => void;
  onDismiss?: () => void;
}

type PropertyRowKey =
  | 'name'
  | 'template'
  | 'duration'
  | 'visitMode'
  | 'billing'
  | 'workflow'
  | 'labels';

const APPOINTMENT_REMINDER = {
  id: 'appointment-reminder',
  reminder_template_id: 'appointment-reminder',
  name: 'Appointment Reminder',
};

const COMMON_DURATIONS = [15, 20, 30, 45, 50, 60] as const;

type AppointmentTypeRuleItem = AttachedRuleItem;

const formatDurationLabel = (duration?: number | null) =>
  duration ? `${duration} min` : 'No default duration';

const formatWorkflowLabel = (forms: FormScheduleItem[], reminders: ReminderItem[]) => {
  const parts = [
    forms.length ? `${forms.length} form${forms.length === 1 ? '' : 's'}` : null,
    reminders.length ? `${reminders.length} reminder${reminders.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'Not sending anything automatically';
};

const AppointmentTypeModal: React.FC<AppointmentTypeModalProps> = ({
  open,
  onClose,
  onSave,
  type,
  initialName = '',
  defaultValues,
  appointmentType,
  existingAppointmentTypes = [],
  scope = 'provider',
  organizationId,
  canDelete = true,
  inline = false,
  onBack,
  onDismiss,
}) => {
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  // The scope prop reflects the calling UI's context, but org-owned records
  // reach edit mode from provider-context UIs too (sidebar, picker) — in edit
  // mode the record's owner_scope is authoritative for routing and labels.
  const effectiveScope: 'provider' | 'organization' =
    type === 'edit' && appointmentType?.owner_scope
      ? appointmentType.owner_scope === 'organization'
        ? 'organization'
        : 'provider'
      : scope;
  const effectiveOrganizationId = organizationId ?? appointmentType?.organization_id ?? null;
  // Org-owned types require manage_appointment_types to mutate (enforced
  // server-side on every route); without it the modal renders read-only.
  const canMutateRecord =
    type !== 'edit' || canMutateAppointmentType(appointmentType, canManageAppointmentTypes);
  const providerCreate = useCreateAppointmentType();
  const organizationCreate = useCreateOrganizationAppointmentType(effectiveOrganizationId);
  const providerUpdate = useUpdateAppointmentType();
  const organizationUpdate = useUpdateOrganizationAppointmentType(effectiveOrganizationId);
  const providerDelete = useDeleteAppointmentType();
  const organizationDelete = useDeleteOrganizationAppointmentType(effectiveOrganizationId);
  const createNewAppointmentType =
    effectiveScope === 'organization'
      ? organizationCreate.createNewAppointmentType
      : providerCreate.createNewAppointmentType;
  const updateAppointmentType =
    effectiveScope === 'organization'
      ? organizationUpdate.updateAppointmentType
      : providerUpdate.updateAppointmentType;
  const deleteAppointmentType =
    effectiveScope === 'organization'
      ? organizationDelete.deleteAppointmentType
      : providerDelete.deleteAppointmentType;
  const isCreating =
    effectiveScope === 'organization' ? organizationCreate.isCreating : providerCreate.isCreating;
  const isUpdating =
    effectiveScope === 'organization' ? organizationUpdate.isUpdating : providerUpdate.isUpdating;
  const isDeleting =
    effectiveScope === 'organization' ? organizationDelete.isDeleting : providerDelete.isDeleting;
  const availableColors = useAvailableColors();
  const { appointment, error: trackError } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccess: hasTelehealthAccess } = useTelehealthAccess();
  const { open: openModal } = useModal();
  const templatesList = useAppointmentTypeTemplates();
  const { singular: subjectLabel } = useSubjectLanguageVariants();

  // Lookup telehealth addon intro offer for dynamic badge/description
  const { addOns } = useBundleCatalog();
  const telehealthIntroOffer = useMemo(() => {
    const addon = addOns.find(b => b.grants?.some(g => g.capability_name === 'telehealth'));
    return addon?.intro_offer ?? null;
  }, [addOns]);

  const templateNameById = useTemplateNameById();

  const isLoading = type === 'create' ? isCreating : isUpdating;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [enterKeyReleased, setEnterKeyReleased] = useState(true);

  const [formData, setFormData] = useState<AppointmentTypeFormData>({
    name: '',
    color: 'color1',
    defaultTemplateId: null,
    defaultTemplate: null,
    defaultDuration: null,
    telehealth: false,
    defaultModality: null,
    defaultUseJotmeet: false,
    defaultPlaceOfService: null,
    defaultCptCodes: [],
    selectedForms: [],
    selectedReminders: [],
    defaultLabelIds: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialColor, setInitialColor] = useState<string | null>(null);
  // Accordion: at most one property row expanded at a time.
  const [expandedRow, setExpandedRow] = useState<PropertyRowKey | null>(
    type === 'create' ? 'name' : null,
  );
  const toggleRow = (row: PropertyRowKey) => setExpandedRow(prev => (prev === row ? null : row));

  // Track modal opened
  useEffect(() => {
    if (open) {
      appointment.typeModalOpened({
        mode: type,
        appointment_type_id: type === 'edit' ? appointmentType?.encounter_type_id : undefined,
      });
    }
  }, [open, type, appointmentType?.encounter_type_id, appointment]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setExpandedRow(type === 'create' ? 'name' : null);
      if (type === 'create') {
        // Extract values with overrides taking precedence
        const baseType = defaultValues?.basedOnAppointmentType;
        const overrides = defaultValues?.overrides;

        const templateId = overrides?.templateId ?? baseType?.template_id ?? null;
        const duration = overrides?.duration ?? baseType?.default_duration ?? null;
        const telehealth = overrides?.telehealth ?? baseType?.telehealth ?? false;
        const color = baseType?.color || 'color1';
        const defaultCptCodes = normalizeCptCodes(baseType?.default_cpt_codes ?? []);
        const defaultModality = baseType?.default_modality ?? null;
        const defaultUseJotmeet = Boolean(
          (overrides?.useJotmeet ?? baseType?.default_use_jotmeet) && defaultModality === 'video',
        );

        // For forms and reminders, use overrides if provided, otherwise extract from baseType
        const forms =
          overrides?.forms ??
          (baseType?.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) ||
            []);

        const reminders =
          overrides?.reminders ??
          (baseType?.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id:
              rule.reminder_template_id || APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) ||
            []);

        setFormData({
          name: initialName,
          color: color,
          defaultTemplateId: templateId,
          defaultTemplate: null,
          defaultDuration: duration,
          telehealth: telehealth,
          defaultModality,
          defaultUseJotmeet,
          defaultPlaceOfService: baseType?.default_place_of_service ?? null,
          defaultCptCodes,
          defaultLabelIds: baseType?.default_label_ids ?? [],
          selectedForms: forms.map(form => ({
            ...form,
            form_definition_id: (form as FormOverrideItem).form_definition_id || form.formId || '',
            offset: form.offset || '',
          })),
          selectedReminders: reminders.map(reminder => ({
            ...reminder,
            reminder_template_id:
              (reminder as ReminderOverrideItem).reminder_template_id ||
              APPOINTMENT_REMINDER.reminder_template_id,
            offset: reminder.offset || '',
          })),
        });
        setInitialColor(color);
      } else if (type === 'edit' && appointmentType) {
        // Edit mode - populate from existing appointment type
        const forms =
          appointmentType.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) || [];

        const reminders =
          appointmentType.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id:
              rule.reminder_template_id || APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) || [];

        const currentColor = appointmentType.color || 'color1';
        setFormData({
          name: appointmentType.encounter_type,
          color: currentColor,
          defaultTemplateId: appointmentType.template_id || null,
          defaultTemplate: null,
          defaultDuration: appointmentType.default_duration || null,
          telehealth: appointmentType.telehealth || false,
          defaultModality: appointmentType.default_modality ?? null,
          defaultUseJotmeet: Boolean(
            appointmentType.default_use_jotmeet && appointmentType.default_modality === 'video',
          ),
          defaultPlaceOfService: appointmentType.default_place_of_service ?? null,
          defaultCptCodes: normalizeCptCodes(appointmentType.default_cpt_codes ?? []),
          defaultLabelIds: appointmentType.default_label_ids ?? [],
          selectedForms: forms,
          selectedReminders: reminders,
        });
        setInitialColor(currentColor);
      }
      setErrors({});
      setSaveError(null);
    }
  }, [open, type, initialName, defaultValues, appointmentType]);

  const handleSave = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    setSaveError(null);

    if (!formData.name.trim()) {
      newErrors.name = 'Appointment type name is required';
    } else {
      // Check for duplicate name (case-insensitive), excluding current type when editing
      const trimmedName = formData.name.trim().toLowerCase();
      const isDuplicate = existingAppointmentTypes.some(
        at =>
          at.encounter_type.toLowerCase() === trimmedName &&
          (type !== 'edit' || at.encounter_type_id !== appointmentType?.encounter_type_id),
      );
      if (isDuplicate) {
        newErrors.name = `An appointment type named "${formData.name.trim()}" already exists`;
      }
    }

    const billingError = getModalityPlaceOfServiceError({
      modality: formData.defaultModality,
      placeOfService: formData.defaultPlaceOfService,
    });
    if (billingError) {
      newErrors.billing = billingError;
    }
    if (formData.defaultUseJotmeet && formData.defaultModality !== 'video') {
      newErrors.visitMode = 'JotMeet defaults require video visit mode.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (newErrors.name) {
        setExpandedRow('name');
      } else if (newErrors.visitMode) {
        setExpandedRow('visitMode');
      } else if (newErrors.billing) {
        setExpandedRow('billing');
      }
      return;
    }

    if (effectiveScope === 'organization' && !effectiveOrganizationId) {
      setSaveError('Organization information is still loading. Please try again.');
      return;
    }

    if (type === 'create') {
      // Create mode
      const appointmentTypeData: CreateAppointmentTypeRequest = {
        encounter_type: formData.name,
        color: formData.color,
        // Send null (not undefined) so an unset default persists as cleared — an
        // omitted key is treated as "no change" by the API.
        default_duration: formData.defaultDuration,
        telehealth: formData.telehealth,
        default_modality: formData.defaultModality,
        default_use_jotmeet: formData.defaultUseJotmeet && formData.defaultModality === 'video',
        default_place_of_service: formData.defaultPlaceOfService,
        default_cpt_codes: formData.defaultCptCodes,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
        default_label_ids: formData.defaultLabelIds,
      };

      try {
        const result = await createNewAppointmentType(appointmentTypeData);
        // Track successful creation
        appointment.typeCreated({
          appointment_type_id: result.encounter_type_id,
          color_selected: formData.color,
          has_default_duration: !!formData.defaultDuration,
          has_default_template: !!formData.defaultTemplateId,
          has_default_forms: formData.selectedForms.length > 0,
          has_default_reminders: formData.selectedReminders.length > 0,
          has_default_cpt_codes: formData.defaultCptCodes.length > 0,
          is_telehealth: formData.telehealth,
        });

        enqueueSnackbar(
          effectiveScope === 'organization'
            ? 'Organization appointment type created successfully'
            : 'Appointment type created successfully',
          { variant: 'success' },
        );

        if (onSave) {
          onSave(result);
        }
        onClose();
      } catch (e: unknown) {
        const apiMessage = getApiErrorMessage(e);
        if (apiMessage.toLowerCase().includes('already exists')) {
          setErrors({ name: `An appointment type named "${formData.name}" already exists` });
        } else {
          setSaveError(apiMessage || 'Failed to create appointment type. Please try again.');
          trackError.track(e as Error, 'appointment_type_creation', {
            color_selected: formData.color,
            has_default_duration: String(!!formData.defaultDuration),
          });
        }
      }
    } else if (type === 'edit' && appointmentType) {
      // Edit mode
      const nameChanged = formData.name !== appointmentType.encounter_type;
      const updateData: UpdateAppointmentTypeRequest = {
        ...(nameChanged && { encounter_type: formData.name }),
        color: formData.color,
        // Send null (not undefined) so clearing a default actually persists — an
        // omitted key is treated as "no change" by the PATCH.
        default_duration: formData.defaultDuration,
        telehealth: formData.telehealth,
        default_modality: formData.defaultModality,
        default_use_jotmeet: formData.defaultUseJotmeet && formData.defaultModality === 'video',
        default_place_of_service: formData.defaultPlaceOfService,
        default_cpt_codes: formData.defaultCptCodes,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
        default_label_ids: formData.defaultLabelIds,
      };

      // Determine what fields changed
      const fieldsChanged: string[] = [];
      if (formData.name !== appointmentType.encounter_type) fieldsChanged.push('name');
      if (formData.color !== initialColor) fieldsChanged.push('color');
      if (formData.defaultDuration !== appointmentType.default_duration)
        fieldsChanged.push('duration');
      if (formData.telehealth !== appointmentType.telehealth) fieldsChanged.push('telehealth');
      if (formData.defaultTemplateId !== appointmentType.template_id)
        fieldsChanged.push('template');
      if (formData.defaultModality !== (appointmentType.default_modality ?? null))
        fieldsChanged.push('modality');
      if (formData.defaultUseJotmeet !== Boolean(appointmentType.default_use_jotmeet))
        fieldsChanged.push('default_use_jotmeet');
      if (formData.defaultPlaceOfService !== (appointmentType.default_place_of_service ?? null))
        fieldsChanged.push('place_of_service');
      if (!doCptCodesMatch(formData.defaultCptCodes, appointmentType.default_cpt_codes))
        fieldsChanged.push('cpt_codes');
      if (
        JSON.stringify(formData.selectedForms) !==
        JSON.stringify(appointmentType.default_form_rules)
      )
        fieldsChanged.push('forms');
      if (
        JSON.stringify(formData.selectedReminders) !==
        JSON.stringify(appointmentType.default_reminder_rules)
      )
        fieldsChanged.push('reminders');

      try {
        const result = await updateAppointmentType(appointmentType.encounter_type_id, updateData);
        // Track successful update
        appointment.typeUpdated({
          appointment_type_id: appointmentType.encounter_type_id,
          color_changed: formData.color !== initialColor,
          color_selected: formData.color,
          fields_changed: fieldsChanged,
        });

        enqueueSnackbar(
          effectiveScope === 'organization'
            ? 'Organization appointment type updated successfully'
            : 'Appointment type updated successfully',
          { variant: 'success' },
        );

        if (onSave) {
          onSave(result);
        }
        onClose();
      } catch (e: unknown) {
        const apiMessage = getApiErrorMessage(e);
        if (apiMessage.toLowerCase().includes('already exists')) {
          setErrors({ name: `An appointment type named "${formData.name}" already exists` });
        } else {
          setSaveError(apiMessage || 'Failed to update appointment type. Please try again.');
          trackError.track(e as Error, 'appointment_type_update', {
            appointment_type_id: appointmentType.encounter_type_id,
            fields_changed: fieldsChanged.join(','),
          });
        }
      }
    }
  };

  const handleDeleteClick = () => {
    if (appointmentType) {
      appointment.typeDeleteClicked({
        appointment_type_id: appointmentType.encounter_type_id,
      });
      setShowDeleteConfirm(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!appointmentType) return;

    if (effectiveScope === 'organization' && !effectiveOrganizationId) {
      setShowDeleteConfirm(false);
      enqueueSnackbar('Organization information is still loading. Please try again.', {
        variant: 'error',
      });
      return;
    }

    try {
      const result = await deleteAppointmentType(appointmentType.encounter_type_id);
      if (result.success) {
        appointment.typeDeleted({
          appointment_type_id: appointmentType.encounter_type_id,
        });
        enqueueSnackbar(
          effectiveScope === 'organization'
            ? 'Organization appointment type deleted successfully'
            : 'Appointment type deleted successfully',
          { variant: 'success' },
        );
        setShowDeleteConfirm(false);
        onClose();
      } else {
        setShowDeleteConfirm(false);
        enqueueSnackbar(result.message || 'Failed to delete appointment type', {
          variant: 'error',
        });
      }
    } catch (e) {
      trackError.track(e as Error, 'appointment_type_delete', {
        appointment_type_id: appointmentType.encounter_type_id,
      });
      enqueueSnackbar('Failed to delete appointment type', { variant: 'error' });
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: UserTemplateItem | undefined) => {
    setFormData(prev => ({
      ...prev,
      defaultTemplateId: template?.template_id || null,
      defaultTemplate: template || null,
    }));
  };

  const handleModalityChange = useCallback((value: string) => {
    const modality = (value as 'in_person' | 'video' | 'audio') || null;
    // Telehealth means a virtual visit — video or audio (phone). Selecting either
    // sets it; only in-person (or "no default") clears it.
    setFormData(prev => ({
      ...prev,
      defaultModality: modality,
      telehealth: modality === 'video' || modality === 'audio',
      defaultUseJotmeet: modality === 'video' ? prev.defaultUseJotmeet : false,
    }));
  }, []);

  const billingConflictError = getModalityPlaceOfServiceError({
    modality: formData.defaultModality,
    placeOfService: formData.defaultPlaceOfService,
  });
  const visitModeConflictError =
    formData.defaultUseJotmeet && formData.defaultModality !== 'video'
      ? 'JotMeet defaults require video visit mode.'
      : null;
  const submitBlockedReason = !formData.name.trim()
    ? 'Enter appointment type name'
    : billingConflictError || visitModeConflictError || '';
  const canSubmit =
    Boolean(formData.name.trim()) && !billingConflictError && !visitModeConflictError;

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        formData.name.trim() &&
        canSubmit &&
        !isLoading
      ) {
        event.preventDefault();
        handleSave();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  // Don't render if edit mode but no appointment type
  if (type === 'edit' && !appointmentType) {
    return null;
  }

  const scopeLabel =
    effectiveScope === 'organization' ? 'Practice Appointment Type' : 'Appointment Type';
  const dialogTitle =
    type === 'create' ? `New ${scopeLabel}` : formData.name.trim() || `Edit ${scopeLabel}`;
  const submitButtonText = type === 'create' ? `Create ${scopeLabel}` : `Update ${scopeLabel}`;
  const loadingText = type === 'create' ? 'Creating...' : 'Updating...';
  const templateLabel =
    formData.defaultTemplate?.display_name ??
    (formData.defaultTemplateId
      ? (templateNameById.get(formData.defaultTemplateId) ?? 'Note template selected')
      : 'No default template');

  const content = (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 2,
        }}
      >
        {inline && (
          <IconButton
            onClick={onBack ?? onClose}
            aria-label="Back to appointment types"
            edge="start"
            size="small"
            sx={{ mt: 0.25 }}
          >
            <ArrowBack />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 650, lineHeight: 1.25 }}>
            {dialogTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: ELIG_TOKENS.inkSoft }}>
            Defaults used when this appointment type is selected.
          </Typography>
        </Box>
        <IconButton
          onClick={onDismiss ?? onClose}
          aria-label="Close"
          edge="end"
          size="small"
          sx={{ ml: 'auto' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 2,
          borderColor: ELIG_TOKENS.line,
          ...(inline
            ? {
                maxHeight: { xs: 'calc(100vh - 168px)', sm: '68vh' },
                minHeight: { sm: 420 },
                overflowY: 'auto',
              }
            : {}),
        }}
      >
        <Stack spacing={1.5}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {!canMutateRecord && (
            <Alert severity="info">
              This appointment type is managed by your practice. Need a new type or a change? Ask a
              practice admin.
            </Alert>
          )}

          <Box>
            <Typography
              variant="overline"
              sx={{ color: ELIG_TOKENS.inkFaint, fontWeight: 700, letterSpacing: 0.6 }}
            >
              Properties
            </Typography>
            <AppointmentTypePropertyRow
              icon={PaletteOutlined}
              label="Name & color"
              helper="The name and color your team sees in the type picker and on every calendar block of this type."
              disabled={!canMutateRecord}
              open={expandedRow === 'name'}
              onToggle={() => toggleRow('name')}
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <ColorDot colorEnum={formData.color} size={12} />
                  {formData.name.trim() ? (
                    <PlainPropertyValue>{formData.name.trim()}</PlainPropertyValue>
                  ) : (
                    <EmptyPropertyValue>Required</EmptyPropertyValue>
                  )}
                </Box>
              }
            >
              <Stack spacing={1.25} sx={{ p: 1.25 }}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  autoFocus={type === 'create'}
                  required
                  size="small"
                />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(28px, 1fr))',
                    gap: 0.75,
                    maxWidth: 360,
                  }}
                >
                  {availableColors.map(option => (
                    <Tooltip key={option.value} title={option.label}>
                      <ButtonBase
                        onClick={() => {
                          const previousColor = formData.color;
                          setFormData(prev => ({ ...prev, color: option.value }));
                          appointment.typeColorChanged({
                            appointment_type_id:
                              type === 'edit' ? appointmentType?.encounter_type_id : undefined,
                            previous_color: previousColor,
                            new_color: option.value,
                            context: type,
                          });
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.color === option.value
                              ? ELIG_TOKENS.midnight
                              : ELIG_TOKENS.lineStrong
                          }`,
                          bgcolor:
                            formData.color === option.value
                              ? 'rgba(28, 30, 133, 0.06)'
                              : 'transparent',
                        }}
                      >
                        <ColorDot colorEnum={option.value} size={16} />
                      </ButtonBase>
                    </Tooltip>
                  ))}
                </Box>
              </Stack>
            </AppointmentTypePropertyRow>

            <AppointmentTypePropertyRow
              icon={ArticleOutlined}
              label="Note template"
              helper="The template the AI uses to write the note for every encounter of this type. Clinicians can still switch templates per session."
              disabled={!canMutateRecord}
              open={expandedRow === 'template'}
              onToggle={() => toggleRow('template')}
              value={<PlainPropertyValue>{templateLabel}</PlainPropertyValue>}
            >
              <TemplatePropertyEditor
                value={formData.defaultTemplateId}
                templates={templatesList}
                onChange={template => handleTemplateSelect(template)}
              />
            </AppointmentTypePropertyRow>

            <AppointmentTypePropertyRow
              icon={AccessTimeOutlined}
              label="Duration"
              helper="The default length for every appointment of this type — adjustable on each appointment when scheduling."
              disabled={!canMutateRecord}
              open={expandedRow === 'duration'}
              onToggle={() => toggleRow('duration')}
              value={
                <PlainPropertyValue>
                  {formatDurationLabel(formData.defaultDuration)}
                </PlainPropertyValue>
              }
            >
              <AppointmentTypeDurationEditor
                value={formData.defaultDuration}
                onChange={duration => setFormData(prev => ({ ...prev, defaultDuration: duration }))}
              />
            </AppointmentTypePropertyRow>

            <AppointmentTypePropertyRow
              icon={VideocamOutlined}
              label="Visit mode"
              helper="How visits of this type happen by default — in-person, phone, or video. Pre-fills each appointment and is checked against the billing place of service."
              disabled={!canMutateRecord}
              open={expandedRow === 'visitMode'}
              onToggle={() => toggleRow('visitMode')}
              error={visitModeConflictError}
              value={
                <PlainPropertyValue>
                  {[
                    formatVisitModeLabel(formData.defaultModality, formData.telehealth),
                    formData.defaultUseJotmeet && formData.defaultModality === 'video'
                      ? 'JotMeet'
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </PlainPropertyValue>
              }
            >
              <VisitModeEditor
                modality={formData.defaultModality}
                hasTelehealthAccess={hasTelehealthAccess}
                defaultUseJotmeet={formData.defaultUseJotmeet}
                error={visitModeConflictError || errors.visitMode}
                telehealthOfferLabel={
                  telehealthIntroOffer?.percent_off === 100 ? 'First month free' : undefined
                }
                onModalityChange={handleModalityChange}
                onDefaultUseJotmeetChange={defaultUseJotmeet =>
                  setFormData(prev => ({ ...prev, defaultUseJotmeet }))
                }
                onOpenTelehealthUpsell={() =>
                  openModal(MODAL_TYPES.ADDON_UPSELL, {
                    addonId: 'jotmeet',
                    sx: { borderRadius: 3, overflow: 'hidden' },
                  })
                }
              />
            </AppointmentTypePropertyRow>

            <AppointmentTypePropertyRow
              icon={ReceiptLongOutlined}
              label="Billing defaults"
              helper="Place of service and CPT codes that pre-fill billing for every appointment of this type. Diagnosis (ICD-10) is still set per encounter."
              disabled={!canMutateRecord}
              open={expandedRow === 'billing'}
              onToggle={() => toggleRow('billing')}
              alignTop
              error={billingConflictError}
              value={
                <PlainPropertyValue>
                  {formatBillingDefaultsLabel(
                    formData.defaultPlaceOfService,
                    formData.defaultCptCodes,
                  )}
                </PlainPropertyValue>
              }
            >
              <BillingDefaultsEditor
                placeOfService={formData.defaultPlaceOfService}
                cptCodes={formData.defaultCptCodes}
                error={billingConflictError || errors.billing}
                onPlaceOfServiceChange={placeOfService =>
                  setFormData(prev => ({ ...prev, defaultPlaceOfService: placeOfService }))
                }
                onCptCodesChange={codes =>
                  setFormData(prev => ({ ...prev, defaultCptCodes: normalizeCptCodes(codes) }))
                }
              />
            </AppointmentTypePropertyRow>

            <AppointmentTypePropertyRow
              icon={AssignmentTurnedInOutlined}
              label="Forms & reminders"
              helper={`Forms and reminders set here go to the ${subjectLabel} automatically before every appointment of this type — you don't send them by hand.`}
              disabled={!canMutateRecord}
              open={expandedRow === 'workflow'}
              onToggle={() => toggleRow('workflow')}
              alignTop
              value={
                <PlainPropertyValue>
                  {formatWorkflowLabel(formData.selectedForms, formData.selectedReminders)}
                </PlainPropertyValue>
              }
            >
              <WorkflowDefaultsEditor
                forms={formScheduleItemsToRuleItems(formData.selectedForms)}
                reminders={reminderItemsToRuleItems(formData.selectedReminders)}
                onFormsChange={forms =>
                  setFormData(prev => ({
                    ...prev,
                    selectedForms: ruleItemsToFormScheduleItems(forms),
                  }))
                }
                onRemindersChange={reminders =>
                  setFormData(prev => ({
                    ...prev,
                    selectedReminders: ruleItemsToReminderItems(reminders),
                  }))
                }
              />
            </AppointmentTypePropertyRow>

            {/* Default labels are an org-scoped concern: only organization-owned
                appointment types expose this field. Provider-scoped (personal)
                types inherit the org type's defaults but can't set their own here
                — intentional for now; revisit if per-provider defaults are wanted. */}
            {effectiveScope === 'organization' && effectiveOrganizationId && (
              <AppointmentTypePropertyRow
                icon={LabelOutlined}
                label="Default labels"
                helper="Labels applied automatically to every new appointment of this type — they color-code and group the calendar, without changing any defaults."
                disabled={!canMutateRecord}
                open={expandedRow === 'labels'}
                onToggle={() => toggleRow('labels')}
                alignTop
                value={
                  <PlainPropertyValue>
                    {formData.defaultLabelIds.length > 0
                      ? `${formData.defaultLabelIds.length} label${
                          formData.defaultLabelIds.length === 1 ? '' : 's'
                        }`
                      : 'None'}
                  </PlainPropertyValue>
                }
              >
                <LabelPicker
                  organizationId={effectiveOrganizationId}
                  value={formData.defaultLabelIds}
                  onChange={labelIds =>
                    setFormData(prev => ({ ...prev, defaultLabelIds: labelIds }))
                  }
                  trackingContext={{
                    scope: 'appointment_type',
                    surface: 'appointment_type_modal',
                  }}
                />
              </AppointmentTypePropertyRow>
            )}

            <AppointmentTypePropertyRow
              icon={LocalHospitalOutlined}
              label="Scope"
              disabled
              value={
                <PlainPropertyValue>
                  {effectiveScope === 'organization' ? 'Practice-wide' : 'Only my appointments'}
                </PlainPropertyValue>
              }
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {type === 'edit' && canDelete && canMutateRecord && (
            <Button
              color="error"
              size="small"
              onClick={handleDeleteClick}
              disabled={isLoading || isDeleting}
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit" size="small" disabled={isLoading}>
            {canMutateRecord ? 'Cancel' : 'Close'}
          </Button>
          {canMutateRecord && (
            <Tooltip title={isLoading ? '' : submitBlockedReason} placement="top">
              <span>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!canSubmit || isLoading}
                    sx={{
                      color: 'white',
                      pr: formData.name.trim() && !isLoading ? 3.5 : 2,
                    }}
                    startIcon={isLoading ? <CircularProgress size={16} /> : null}
                  >
                    {isLoading ? loadingText : submitButtonText}
                  </Button>
                  {formData.name.trim() && !isLoading && (
                    <KeyboardReturnIcon
                      fontSize="small"
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '1rem',
                        opacity: 0.7,
                        color: 'white',
                      }}
                    />
                  )}
                </Box>
              </span>
            </Tooltip>
          )}
        </Box>
      </DialogActions>

      <ConfirmDialog
        open={showDeleteConfirm}
        variant="warning"
        title="Delete Appointment Type?"
        message={`Are you sure you want to delete "${appointmentType?.encounter_type || ''}"? This action cannot be undone.`}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />
    </>
  );

  if (inline) {
    return (
      <Box sx={{ display: 'contents' }} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
        {content}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '88vh',
          bgcolor: ELIG_TOKENS.bg,
          color: ELIG_TOKENS.ink,
        },
      }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      {content}
    </Dialog>
  );
};

const AppointmentTypePropertyRow = ({
  icon: Icon,
  label,
  value,
  children,
  disabled = false,
  open = false,
  onToggle,
  alignTop = false,
  error,
  helper,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  /** Controlled by the parent so only one row expands at a time. */
  open?: boolean;
  onToggle?: () => void;
  alignTop?: boolean;
  error?: string | null;
  /** Consequence copy shown at the top of the expanded editor. */
  helper?: string;
}) => {
  const isOpen = open && !disabled;
  const isInteractive = Boolean(children) && !disabled && Boolean(onToggle);
  const hasError = Boolean(error);

  return (
    <Box
      sx={{
        borderTop: `1px solid ${ELIG_TOKENS.line}`,
        '&:last-of-type': {
          borderBottom: `1px solid ${ELIG_TOKENS.line}`,
        },
      }}
    >
      <Box
        component={isInteractive ? 'button' : 'div'}
        type={isInteractive ? 'button' : undefined}
        onClick={isInteractive ? onToggle : undefined}
        aria-invalid={hasError || undefined}
        sx={{
          display: 'grid',
          gridTemplateColumns: '128px minmax(0, 1fr)',
          alignItems: alignTop ? 'flex-start' : 'center',
          gap: 1,
          width: '100%',
          px: 0.5,
          py: 0.8,
          border: 0,
          borderRadius: 1,
          borderLeft: hasError ? '3px solid' : '3px solid transparent',
          borderLeftColor: hasError ? 'error.main' : 'transparent',
          bgcolor: hasError
            ? 'rgba(211, 47, 47, 0.06)'
            : isOpen
              ? 'rgba(0,0,0,0.035)'
              : 'transparent',
          color: 'inherit',
          textAlign: 'left',
          cursor: isInteractive ? 'pointer' : 'default',
          transition: 'background-color 0.12s',
          '&:hover': isInteractive
            ? {
                bgcolor: hasError
                  ? 'rgba(211, 47, 47, 0.08)'
                  : isOpen
                    ? 'rgba(0,0,0,0.045)'
                    : 'rgba(0,0,0,0.025)',
              }
            : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: hasError ? 'error.main' : ELIG_TOKENS.inkSoft,
          }}
        >
          <Icon sx={{ fontSize: 14, mt: alignTop ? 0.25 : 0 }} />
          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {value}
          {hasError && !isOpen ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                color: 'error.main',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.25,
              }}
            >
              {error}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {isOpen && children ? (
        <Box
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          sx={{
            mb: 1,
            borderRadius: 1,
            border: `1px solid ${ELIG_TOKENS.lineStrong}`,
            bgcolor: ELIG_TOKENS.card,
            overflow: 'hidden',
          }}
        >
          {helper ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1.5,
                py: 0.75,
                color: ELIG_TOKENS.inkSoft,
                fontSize: 11,
                lineHeight: 1.4,
                borderBottom: `1px solid ${ELIG_TOKENS.line}`,
              }}
            >
              {helper}
            </Typography>
          ) : null}
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

const TemplatePropertyEditor = ({
  value,
  templates,
  onChange,
}: {
  value: string | null;
  templates: UserTemplateItem[];
  onChange: (template: UserTemplateItem | undefined) => void;
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredTemplates = normalizedSearch
    ? templates.filter(template =>
        (template.display_name || '').toLowerCase().includes(normalizedSearch),
      )
    : templates;
  const itemCount = filteredTemplates.length + 1;

  useEffect(() => {
    setActiveIndex(0);
  }, [filteredTemplates.length, search]);

  const commitActive = () => {
    if (activeIndex === 0) {
      onChange(undefined);
      return;
    }

    const template = filteredTemplates[activeIndex - 1];
    if (template) onChange(template);
  };

  return (
    <PropertyEditorSearchFrame
      search={search}
      placeholder="Search note templates..."
      activeIndex={activeIndex}
      itemCount={itemCount}
      onSearchChange={setSearch}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActive}
    >
      <OptionRow selected={activeIndex === 0} onClick={() => onChange(undefined)}>
        <EmptyPropertyValue>No default template</EmptyPropertyValue>
      </OptionRow>
      {filteredTemplates.map((template, index) => (
        <OptionRow
          key={template.template_id}
          selected={activeIndex === index + 1}
          disabled={value === template.template_id}
          onClick={() => onChange(template)}
        >
          <PlainPropertyValue>{template.display_name || 'Untitled template'}</PlainPropertyValue>
        </OptionRow>
      ))}
      {filteredTemplates.length === 0 && search && <EditorHint>No templates match</EditorHint>}
    </PropertyEditorSearchFrame>
  );
};

const AppointmentTypeDurationEditor = ({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
}) => {
  const isCustom = Boolean(value) && !COMMON_DURATIONS.some(duration => duration === value);
  const [customDraft, setCustomDraft] = useState(isCustom && value ? String(value) : '');

  useEffect(() => {
    setCustomDraft(isCustom && value ? String(value) : '');
  }, [isCustom, value]);

  const commitCustom = () => {
    const duration = parseInt(customDraft, 10);
    if (Number.isFinite(duration) && duration > 0) {
      onChange(duration);
    } else {
      setCustomDraft(isCustom && value ? String(value) : '');
    }
  };

  return (
    <EditorOptionList>
      <OptionRow selected={value == null} onClick={() => onChange(null)}>
        <EmptyPropertyValue>No default duration</EmptyPropertyValue>
      </OptionRow>
      {COMMON_DURATIONS.map(duration => (
        <OptionRow key={duration} selected={value === duration} onClick={() => onChange(duration)}>
          <PlainPropertyValue>{duration} min</PlainPropertyValue>
        </OptionRow>
      ))}
      <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
      <CompactInputRow
        label="Custom"
        value={customDraft}
        placeholder="50"
        suffix="min"
        type="number"
        selected={isCustom}
        onChange={setCustomDraft}
        onCommit={commitCustom}
      />
    </EditorOptionList>
  );
};

const VisitModeEditor = ({
  modality,
  hasTelehealthAccess,
  defaultUseJotmeet,
  error,
  telehealthOfferLabel,
  onModalityChange,
  onDefaultUseJotmeetChange,
  onOpenTelehealthUpsell,
}: {
  modality: 'in_person' | 'video' | 'audio' | null;
  hasTelehealthAccess: boolean;
  defaultUseJotmeet: boolean;
  error?: string;
  telehealthOfferLabel?: string;
  onModalityChange: (value: string) => void;
  onDefaultUseJotmeetChange: (value: boolean) => void;
  onOpenTelehealthUpsell: () => void;
}) => (
  // Visit mode is modality only. JotMeet hosting is a separate video default,
  // and non-entitled users can still discover the video-hosting add-on here.
  <EditorOptionList>
    {[
      { label: 'No default', value: '' },
      { label: 'In-person', value: 'in_person' },
      { label: 'Phone', value: 'audio' },
      { label: 'Video', value: 'video' },
    ].map(option => (
      <OptionRow
        key={option.value || 'none'}
        selected={(modality || '') === option.value}
        onClick={() => onModalityChange(option.value)}
      >
        <PlainPropertyValue>{option.label}</PlainPropertyValue>
      </OptionRow>
    ))}
    {hasTelehealthAccess && modality === 'video' && (
      <>
        <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Checkbox
                checked={defaultUseJotmeet}
                size="small"
                sx={{ p: 0 }}
                onChange={event => onDefaultUseJotmeetChange(event.target.checked)}
              />
              <PlainPropertyValue>Use JotMeet by default</PlainPropertyValue>
            </Stack>
            {error ? (
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                {error}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </>
    )}
    {!hasTelehealthAccess && (
      <>
        <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
        <Box sx={{ px: 1.5, py: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <EmptyPropertyValue>JotMeet can host video visits</EmptyPropertyValue>
            {telehealthOfferLabel ? <Chip label={telehealthOfferLabel} size="small" /> : null}
            <Button size="small" onClick={onOpenTelehealthUpsell}>
              Learn more
            </Button>
          </Stack>
        </Box>
      </>
    )}
  </EditorOptionList>
);

const BillingDefaultsEditor = ({
  placeOfService,
  cptCodes,
  error,
  onPlaceOfServiceChange,
  onCptCodesChange,
}: {
  placeOfService: string | null;
  cptCodes: string[];
  error?: string;
  onPlaceOfServiceChange: (value: string | null) => void;
  onCptCodesChange: (codes: string[]) => void;
}) => (
  <Stack spacing={0} sx={{ bgcolor: ELIG_TOKENS.card }}>
    {error ? (
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
        <Alert severity="error" sx={{ py: 0 }}>
          {error}
        </Alert>
      </Box>
    ) : null}
    <BillingDefaultsFieldRow
      label="Place of service"
      helper={placeOfService ? undefined : 'No default selected'}
    >
      <PlaceOfServiceSelect
        value={placeOfService ?? ''}
        onChange={event => onPlaceOfServiceChange(event.target.value || null)}
        fullWidth
        sx={{
          minWidth: '100%',
          '& .MuiInputBase-input': {
            padding: '6px 12px',
            paddingRight: '30px',
            fontSize: '0.875rem',
            textAlign: 'left',
          },
          '& .MuiOutlinedInput-root': {
            height: 32,
            bgcolor: '#fff',
          },
        }}
      />
    </BillingDefaultsFieldRow>
    <Box sx={{ height: 1, mx: 1.5, bgcolor: ELIG_TOKENS.line }} />
    <BillingDefaultsFieldRow label="CPT codes" alignTop>
      <CPTCodePicker
        value={cptCodes}
        onChange={onCptCodesChange}
        inputLabel={null}
        showHeader={false}
        showHelperText={false}
        size="small"
        popperSx={{ zIndex: 1600 }}
        textFieldSx={{
          '& .MuiOutlinedInput-root': {
            height: 32,
            bgcolor: '#fff',
            py: 0,
          },
          '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            paddingLeft: '12px !important',
            paddingRight: '30px !important',
          },
          '& .MuiAutocomplete-input': {
            padding: '0 !important',
            fontSize: '0.875rem',
          },
        }}
      />
    </BillingDefaultsFieldRow>
  </Stack>
);

const BillingDefaultsFieldRow = ({
  label,
  helper,
  alignTop = false,
  children,
}: {
  label: string;
  helper?: string;
  alignTop?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '120px minmax(0, 1fr)',
      gap: 1,
      alignItems: alignTop ? 'flex-start' : 'center',
      px: 1.5,
      py: 1,
    }}
  >
    <Box sx={{ pt: alignTop ? 0.55 : 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: ELIG_TOKENS.inkSoft,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {helper ? (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.35, color: ELIG_TOKENS.inkFaint, fontSize: 11 }}
        >
          {helper}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const WorkflowDefaultsEditor = ({
  forms,
  reminders,
  onFormsChange,
  onRemindersChange,
}: {
  forms: AppointmentTypeRuleItem[];
  reminders: AppointmentTypeRuleItem[];
  onFormsChange: (items: AppointmentTypeRuleItem[]) => void;
  onRemindersChange: (items: AppointmentTypeRuleItem[]) => void;
}) => {
  const { singular: subjectLabel } = useSubjectLanguageVariants();

  return (
    <Stack spacing={0} sx={{ bgcolor: ELIG_TOKENS.card }}>
      <WorkflowSectionLabel>Forms</WorkflowSectionLabel>
      <WorkflowSectionHint>
        Each form is delivered to the {subjectLabel} at the time you pick. Default: 1 day before.
      </WorkflowSectionHint>
      <FormsRuleEditor value={forms} surface="appointment_type" onChange={onFormsChange} />
      <WorkflowSectionLabel>Reminders</WorkflowSectionLabel>
      <WorkflowSectionHint>
        A text reminder goes to the {subjectLabel} before the appointment.
      </WorkflowSectionHint>
      <RemindersRuleEditor
        value={reminders}
        surface="appointment_type"
        reminderTemplate={{
          templateId: APPOINTMENT_REMINDER.reminder_template_id,
          title: APPOINTMENT_REMINDER.name,
        }}
        onChange={onRemindersChange}
      />
    </Stack>
  );
};

const WorkflowSectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      px: 1.5,
      pt: 1,
      pb: 0.5,
      color: ELIG_TOKENS.inkFaint,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

const WorkflowSectionHint = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ px: 1.5, pb: 0.5, color: ELIG_TOKENS.inkFaint, fontSize: 11, lineHeight: 1.4 }}
  >
    {children}
  </Typography>
);

const OptionRow = ({
  selected,
  disabled = false,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <ButtonBase
    disabled={disabled}
    onClick={event => {
      event.stopPropagation();
      if (!disabled) onClick();
    }}
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 0.75,
      color: ELIG_TOKENS.ink,
      textAlign: 'left',
      bgcolor: selected ? 'rgba(28, 30, 133, 0.06)' : 'transparent',
      '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.035)' },
      '&.Mui-disabled': {
        color: ELIG_TOKENS.inkFaint,
      },
    }}
  >
    {children}
  </ButtonBase>
);

const CompactInputRow = ({
  label,
  value,
  selected,
  type,
  suffix,
  placeholder,
  onChange,
  onCommit,
}: {
  label: string;
  value: string;
  selected: boolean;
  type: 'number' | 'text';
  suffix?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onCommit: () => void;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      bgcolor: selected ? 'rgba(28, 30, 133, 0.05)' : 'transparent',
    }}
  >
    <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.inkSoft }}>
      {label}
    </Typography>
    <InputBase
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={event => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      onClick={event => event.stopPropagation()}
      sx={{
        width: 64,
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        '&.Mui-focused': { borderColor: ELIG_TOKENS.midnight },
        '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
        '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }}
    />
    {suffix ? (
      <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
        {suffix}
      </Typography>
    ) : null}
  </Box>
);

const EditorHint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }}>
      {children}
    </Typography>
  </Box>
);

const PlainPropertyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      color: ELIG_TOKENS.ink,
      fontSize: 13,
      lineHeight: 1.35,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const EmptyPropertyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      color: ELIG_TOKENS.inkFaint,
      fontSize: 13,
      fontStyle: 'italic',
      lineHeight: 1.35,
    }}
  >
    {children}
  </Typography>
);

const formScheduleItemsToRuleItems = (items: FormScheduleItem[]): AppointmentTypeRuleItem[] =>
  items.map((item, index) => ({
    id: String(item.id ?? item.form_definition_id ?? `form-${index}`),
    title: item.title || item.name || item.form_definition_id || `Form ${index + 1}`,
    formDefinitionId: item.form_definition_id || item.formId || item.name,
    offset: item.offset || DEFAULT_FORM_OFFSET,
    // Always derive the human-readable timing from the offset — a stored
    // `timing` may be a raw ISO offset (edit-mode init), which must not leak.
    timing: formatRuleTiming(item.offset || DEFAULT_FORM_OFFSET),
  }));

const reminderItemsToRuleItems = (items: ReminderItem[]): AppointmentTypeRuleItem[] =>
  items.map((item, index) => ({
    id: String(item.id ?? item.reminder_template_id ?? `reminder-${index}`),
    title: item.name || APPOINTMENT_REMINDER.name,
    reminderTemplateId: item.reminder_template_id || APPOINTMENT_REMINDER.reminder_template_id,
    offset: item.offset || DEFAULT_REMINDER_OFFSET,
    timing: formatRuleTiming(item.offset || DEFAULT_REMINDER_OFFSET),
  }));

const ruleItemsToFormScheduleItems = (items: AppointmentTypeRuleItem[]): FormScheduleItem[] =>
  items
    .filter(item => item.formDefinitionId)
    .map(item => ({
      id: item.id,
      name: item.title,
      title: item.title,
      form_definition_id: item.formDefinitionId ?? '',
      formId: item.formDefinitionId,
      offset: item.offset,
      timing: item.timing,
      status: 'configured' as const,
    }));

const ruleItemsToReminderItems = (items: AppointmentTypeRuleItem[]): ReminderItem[] =>
  items
    .filter(item => item.reminderTemplateId)
    .map(item => ({
      id: item.id,
      name: item.title || APPOINTMENT_REMINDER.name,
      reminder_template_id: item.reminderTemplateId ?? APPOINTMENT_REMINDER.reminder_template_id,
      offset: item.offset,
      timing: item.timing,
      status: 'configured' as const,
    }));

export default AppointmentTypeModal;
